import { Factory } from "@/data/dao/Factory";
import { UserDao } from "@/data/dao/service/UserDao";
import type { IDevice, IIdentity } from "@/data/model/User";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { Formatter } from "@/portal/ui/Formatter";

export class UserTableController {
    private $element: JQuery;
    private dao: UserDao;

    private dataTable: DataTable<IIdentity>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<UserDao>(UserDao);

        const tableConfig: IDataTableConfig<IIdentity> = {
            columnMapping: [
                {
                    name: "",
                    property: "avatarPath",
                    sortable: false,
                    styles: { width: "5%" },
                    formatter: (m, p) => {
                        return p === undefined
                            ? ""
                            : '<div class="avatar" style="background-image:url(\'' + p + "');\" />";
                    },
                },
                {
                    name: i18n("general.fields.name"),
                    property: "firstName",
                    styles: { width: "15%" },
                    formatter: (m) => {
                        return m.user === undefined && m.firstName === undefined && m.lastName === undefined
                            ? i18n("user.anonymousUser")
                            : Format.htmlEscape(m.firstName + " " + m.lastName);
                    },
                },
                {
                    name: i18n("general.fields.email"),
                    property: "email",
                    styles: { width: "18%" },
                    sortable: false,
                    formatter: (m) => {
                        return m.user === undefined
                            ? m.email === undefined
                                ? ""
                                : Format.htmlEscape(m.email)
                            : Format.htmlEscape(m.user.email);
                    },
                },
                {
                    name: i18n("user.fields.emailConfirmed"),
                    property: "email",
                    styles: { width: "8%" },
                    sortable: false,
                    formatter: (m) => {
                        return m.user === undefined
                            ? i18n("general.no")
                            : m.user.emailConfirmed
                              ? i18n("general.yes")
                              : i18n("general.no");
                    },
                },
                {
                    name: i18n("general.fields.created"),
                    property: "created",
                    styles: { width: "12%" },
                    formatter: (m, p) => DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("user.fields.lastLogin"),
                    property: "lastLogin",
                    styles: { width: "12%" },
                    formatter: (m, p) =>
                        p === undefined ? i18n("general.timeFormat.never") : DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("general.fields.state"),
                    property: "user",
                    styles: { width: "6%" },
                    sortable: false,
                    formatter: (m, p) => (p === undefined ? i18n("user.anonymous") : i18n("user.signedUp")),
                },
                {
                    name: i18n("user.fields.devices"),
                    property: "devices",
                    sortable: false,
                    formatter: (m, p) => {
                        const devices = $('<div class="devices" />');
                        for (const i in p) {
                            const device: IDevice = p[i];
                            devices.append(
                                $(Formatter.deviceFamilyIcon(device.deviceFamily)).attr("title", device.ipAddress)
                            );
                        }
                        return devices;
                    },
                },
                {
                    name: i18n("user.fields.loginProviders"),
                    property: "user.loginProviders",
                    sortable: false,
                    styles: { width: "10%" },
                    formatter: (m, p) => {
                        const providers = $('<div class="providers" />');
                        if (p !== undefined) {
                            for (const i in p) {
                                $(Formatter.providerLink(p[i]))
                                    .appendTo(providers)
                                    .click((e) => {
                                        e.stopPropagation();
                                        if ($(e.currentTarget).attr("href") === "#") e.preventDefault();
                                    });
                            }
                        }

                        return providers;
                    },
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            defaultSortAsc: false,
            defaultSortProperty: "created",
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilterFromOptions(
            "isSignedUp",
            [
                { value: "true", label: i18n("user.onlySignedUpUsers") },
                { value: "false", label: i18n("user.onlyAnonymousUser") },
            ],
            i18n("user.allUsers"),
            "true"
        );

        this.search.addDropdownFilter(
            "userRole",
            Factory.getInstance<UserDao>(UserDao).userRoleDataSource,
            false,
            i18n("user.allRoles"),
            undefined,
            (f) => f.name
        );

        this.dataTable = new DataTable<IIdentity>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = "/user/" + m.id;
            },
            (m) => {
                window.open("/user/" + m.id);
            }
        );

        this.dataTable.listenTo(this.search);
    }
}
